import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ArrowRight, RefreshCw, Building2, KeyRound, CheckCircle2, UserPlus, LogIn, UserX, UserCheck } from 'lucide-react';
import { setAuth } from '@/store/slices/authSlice';
import { setCompany } from '@/store/slices/companySlice';
import { setOwnedCompanies, setActiveCompanyId } from '@/store/slices/workspaceSlice';
import { Button } from '@/components/ui/button';
import { authApi } from '@/api/axios';

const INDIAN_STATES = [
  ['MH','Maharashtra'],['DL','Delhi'],['KA','Karnataka'],['TN','Tamil Nadu'],
  ['GJ','Gujarat'],['RJ','Rajasthan'],['UP','Uttar Pradesh'],['WB','West Bengal'],
  ['AP','Andhra Pradesh'],['TS','Telangana'],['KL','Kerala'],['MP','Madhya Pradesh'],
  ['HR','Haryana'],['PB','Punjab'],['BR','Bihar'],['OR','Odisha'],
];

export default function LoginPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // intent: 'signin' | 'signup' | 'forgot' — chosen upfront, kept separate
  // from the underlying phone/OTP step so a number mismatch (signing in
  // with a brand-new number, or signing up with one that already exists)
  // shows an explicit redirect screen instead of silently switching forms.
  // step: 'choose' | 'phone' | 'otp' | 'password' | 'register' | 'new-password' | 'no-account' | 'already-exists'
  const [intent, setIntent] = useState('signin');
  const [step, setStep] = useState('choose');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [confirmRegisterPassword, setConfirmRegisterPassword] = useState('');
  const [state, setState] = useState('MH');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [devOtp, setDevOtp] = useState('');
  const [userName, setUserName] = useState('');
  const [resetSuccess, setResetSuccess] = useState(false);

  const goToPhone = (chosenIntent) => {
    setIntent(chosenIntent);
    setStep('phone');
    setError(''); setPhone(''); setOtp(''); setDevOtp('');
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (phone.length !== 10) { setError('Enter a valid 10-digit number'); return; }
    setError(''); setLoading(true);
    try {
      const res = await authApi.post('/v1/auth/send-otp', { phone: `+91${phone}` });
      if (res.data.success) {
        setDevOtp(res.data.data?.otp || '');
        setStep('otp');
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to send OTP. Is auth-service running on :3001?');
    } finally { setLoading(false); }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (otp.length !== 6) { setError('Enter 6-digit OTP'); return; }
    setError(''); setLoading(true);
    try {
      if (intent === 'forgot') {
        // The OTP itself is checked server-side when reset-password is
        // submitted — no separate verify call needed here.
        setStep('new-password');
        setLoading(false);
        return;
      }
      const res = await authApi.post('/v1/auth/verify-otp', { phone: `+91${phone}`, otp });
      const data = res.data.data;

      if (intent === 'signin') {
        if (data.is_new_company) {
          // They tried to sign in with a number that has no account —
          // redirect to a clear "create an account?" screen, not silently
          // into the registration form.
          setStep('no-account');
        } else if (data.requires_password) {
          setUserName(data.user_name || '');
          setStep('password');
        }
      } else if (intent === 'signup') {
        if (data.is_new_company) {
          setStep('register');
        } else {
          // Number already has an account — redirect to sign in.
          setUserName(data.user_name || '');
          setStep('already-exists');
        }
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Invalid OTP');
    } finally { setLoading(false); }
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    if (!password) { setError('Enter your password'); return; }
    setError(''); setLoading(true);
    try {
      const res = await authApi.post('/v1/auth/verify-password', { phone: `+91${phone}`, password });
      finishLogin(res.data.data);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Incorrect password');
    } finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!ownerName.trim()) { setError('Enter your full name'); return; }
    if (!companyName.trim()) { setError('Enter your company name'); return; }
    if (registerPassword.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (registerPassword !== confirmRegisterPassword) { setError('Passwords do not match'); return; }
    setError(''); setLoading(true);
    try {
      const res = await authApi.post('/v1/auth/register-company', {
        phone: `+91${phone}`,
        owner_name: ownerName.trim(),
        company_name: companyName.trim(),
        state,
        password: registerPassword,
      });
      finishLogin(res.data.data);
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to create company');
    } finally { setLoading(false); }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (newPassword !== confirmNewPassword) { setError('Passwords do not match'); return; }
    setError(''); setLoading(true);
    try {
      await authApi.post('/v1/auth/reset-password', { phone: `+91${phone}`, otp, new_password: newPassword });
      setIntent('signin');
      setPassword(newPassword);
      setResetSuccess(true);
      setStep('password');
    } catch (err) {
      setError(err.response?.data?.error?.message || 'Failed to reset password');
    } finally { setLoading(false); }
  };

  const finishLogin = (data) => {
    const { access_token, user, company, permissions, owned_companies } = data;
    dispatch(setAuth({ user, companyId: company.id, accessToken: access_token, permissions }));
    dispatch(setCompany(company));
    // Always populate workspace — WorkspaceSwitcher gates on plan, not on length
    dispatch(setOwnedCompanies(owned_companies || []));
    dispatch(setActiveCompanyId(company.id));
    navigate(company.plan ? '/dashboard' : '/select-plan');
  };

  const startForgotPassword = () => {
    setIntent('forgot');
    setStep('phone');
    setOtp(''); setDevOtp(''); setNewPassword(''); setConfirmNewPassword('');
    setError(''); setResetSuccess(false);
  };

  const backToChoose = () => {
    setStep('choose');
    setError(''); setOtp(''); setDevOtp(''); setPassword('');
  };

  return (
    <div className="w-full max-w-sm">
      <div className="flex flex-col items-center mb-8">
        <div className="w-16 h-16 rounded-[20px] bg-[var(--accent)] flex items-center justify-center mb-4 shadow-[var(--shadow-md)]">
          <span className="text-white text-2xl font-black">A</span>
        </div>
        <h1 className="text-[28px] font-bold text-[var(--text-primary)]">AttendX</h1>
        <p className="text-[14px] text-[var(--text-secondary)] mt-1">Owner Dashboard</p>
      </div>

      <div className="bg-[var(--bg-card)] rounded-[var(--radius-xl)] shadow-[var(--shadow-lg)] p-6 border border-[var(--border)]">

        {/* Step 0 — Choose Sign In or Sign Up */}
        {step === 'choose' && (
          <div className="space-y-4">
            <div className="text-center mb-2">
              <h2 className="text-[20px] font-bold text-[var(--text-primary)] mb-0.5">Welcome to AttendX</h2>
              <p className="text-[13px] text-[var(--text-secondary)]">Sign in to your company, or create a new one</p>
            </div>
            <Button type="button" className="w-full" size="lg" onClick={() => goToPhone('signin')}>
              <LogIn size={16}/> Sign In <ArrowRight size={16}/>
            </Button>
            <button type="button" onClick={() => goToPhone('signup')}
              className="w-full h-12 rounded-[12px] border border-[var(--border)] text-[14px] font-semibold text-[var(--text-primary)] hover:bg-[var(--bg-hover)] transition-colors flex items-center justify-center gap-2">
              <UserPlus size={16}/> Create a New Account
            </button>
          </div>
        )}

        {/* Step 1 — Phone */}
        {step === 'phone' && (
          <form onSubmit={handleSendOtp} className="space-y-5">
            <div>
              <h2 className="text-[20px] font-bold text-[var(--text-primary)] mb-0.5">
                {intent === 'forgot' ? 'Reset password' : intent === 'signup' ? 'Create your account' : 'Sign in'}
              </h2>
              <p className="text-[13px] text-[var(--text-secondary)]">
                {intent === 'forgot' ? "Enter your registered mobile number — we'll send an OTP"
                  : intent === 'signup' ? 'Enter your mobile number to get started'
                  : 'Enter your registered mobile number'}
              </p>
            </div>
            <div className="space-y-1.5">
              <label className="text-[13px] font-semibold text-[var(--text-primary)]">Mobile Number</label>
              <div className="flex">
                <div className="h-10 px-3 flex items-center bg-[var(--bg-muted)] border border-r-0 border-[var(--border)] rounded-l-[10px] text-[14px] text-[var(--text-secondary)] font-medium shrink-0">+91</div>
                <input type="tel" maxLength={10} value={phone} onChange={e=>setPhone(e.target.value.replace(/\D/g,''))} placeholder="9876543210" autoFocus
                  className="flex-1 h-10 px-3 bg-[var(--bg-card)] border border-[var(--border)] rounded-r-[10px] text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent transition-all"/>
              </div>
              {error && <p className="text-[12px] text-[var(--danger)]">{error}</p>}
            </div>
            <Button type="submit" className="w-full" loading={loading} size="lg">
              Send OTP <ArrowRight size={16}/>
            </Button>
            <button type="button" onClick={backToChoose}
              className="w-full text-center text-[13px] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors">
              ← Back
            </button>
          </form>
        )}

        {/* Step 2 — OTP */}
        {step === 'otp' && (
          <form onSubmit={handleVerify} className="space-y-5">
            <div>
              <h2 className="text-[20px] font-bold text-[var(--text-primary)] mb-0.5">Verify OTP</h2>
              <p className="text-[13px] text-[var(--text-secondary)]">Sent to +91 {phone}</p>
            </div>
            {devOtp && (
              <div className="p-3 bg-amber-50 dark:bg-amber-900/20 rounded-[10px] border border-amber-200 dark:border-amber-800">
                <p className="text-[12px] font-semibold text-amber-700 dark:text-amber-400">DEV — OTP: <span className="font-black text-[15px] tracking-widest">{devOtp}</span></p>
              </div>
            )}
            <div className="space-y-1.5">
              <label className="text-[13px] font-semibold text-[var(--text-primary)]">6-digit OTP</label>
              <input type="text" maxLength={6} value={otp} onChange={e=>setOtp(e.target.value.replace(/\D/g,''))} placeholder="123456" autoFocus
                className="w-full h-12 px-4 text-center text-[22px] font-bold tracking-[0.4em] bg-[var(--bg-card)] border border-[var(--border)] rounded-[10px] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] transition-all"/>
              {error && <p className="text-[12px] text-[var(--danger)]">{error}</p>}
            </div>
            <Button type="submit" className="w-full" loading={loading} size="lg">
              Verify & Continue <ArrowRight size={16}/>
            </Button>
            <button type="button" onClick={()=>{setStep('phone');setOtp('');setDevOtp('');setError('');}}
              className="w-full text-center text-[13px] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors flex items-center justify-center gap-1.5">
              <RefreshCw size={13}/> Change number
            </button>
          </form>
        )}

        {/* Step — No account found (signed in with a brand-new number) */}
        {step === 'no-account' && (
          <div className="space-y-4 text-center">
            <div className="w-14 h-14 rounded-full bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center mx-auto">
              <UserX size={24} className="text-amber-600 dark:text-amber-400"/>
            </div>
            <div>
              <h2 className="text-[18px] font-bold text-[var(--text-primary)] mb-1">No account found</h2>
              <p className="text-[13px] text-[var(--text-secondary)]">There's no AttendX account for <strong className="text-[var(--text-primary)]">+91 {phone}</strong>.</p>
            </div>
            <Button type="button" className="w-full" size="lg" onClick={() => { setIntent('signup'); setStep('register'); setError(''); }}>
              <UserPlus size={16}/> Create an Account <ArrowRight size={16}/>
            </Button>
            <button type="button" onClick={()=>{setStep('phone');setOtp('');setDevOtp('');setError('');}}
              className="w-full text-center text-[13px] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors">
              ← Try a different number
            </button>
          </div>
        )}

        {/* Step — Already exists (signed up with an existing number) */}
        {step === 'already-exists' && (
          <div className="space-y-4 text-center">
            <div className="w-14 h-14 rounded-full bg-[var(--accent)]/10 flex items-center justify-center mx-auto">
              <UserCheck size={24} className="text-[var(--accent)]"/>
            </div>
            <div>
              <h2 className="text-[18px] font-bold text-[var(--text-primary)] mb-1">Account already exists</h2>
              <p className="text-[13px] text-[var(--text-secondary)]">
                {userName ? <>Welcome back, <strong className="text-[var(--text-primary)]">{userName.split(' ')[0]}</strong> — </> : ''}
                +91 {phone} is already registered.
              </p>
            </div>
            <Button type="button" className="w-full" size="lg" onClick={() => { setIntent('signin'); setStep('password'); setError(''); }}>
              <LogIn size={16}/> Sign In Instead <ArrowRight size={16}/>
            </Button>
            <button type="button" onClick={()=>{setStep('phone');setOtp('');setDevOtp('');setError('');}}
              className="w-full text-center text-[13px] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors">
              ← Try a different number
            </button>
          </div>
        )}

        {/* Step 3 — Password (returning users) */}
        {step === 'password' && (
          <form onSubmit={handlePasswordLogin} className="space-y-5">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-[12px] bg-[var(--accent)]/10 flex items-center justify-center">
                <KeyRound size={20} className="text-[var(--accent)]"/>
              </div>
              <div>
                <h2 className="text-[18px] font-bold text-[var(--text-primary)]">
                  {userName ? `Welcome back, ${userName.split(' ')[0]}` : 'Enter your password'}
                </h2>
                <p className="text-[12px] text-[var(--text-secondary)]">+91 {phone}</p>
              </div>
            </div>
            {resetSuccess && (
              <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-[10px] border border-green-200 dark:border-green-800 flex items-center gap-2">
                <CheckCircle2 size={15} className="text-green-600 dark:text-green-400 shrink-0"/>
                <p className="text-[12px] font-medium text-green-700 dark:text-green-400">Password updated — sign in with your new password.</p>
              </div>
            )}
            <div className="space-y-1.5">
              <label className="text-[13px] font-semibold text-[var(--text-primary)]">Password</label>
              <input type="password" value={password} onChange={e=>setPassword(e.target.value)} placeholder="••••••••" autoFocus
                className="w-full h-10 px-3 bg-[var(--bg-card)] border border-[var(--border)] rounded-[10px] text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] transition-all"/>
              {error && <p className="text-[12px] text-[var(--danger)]">{error}</p>}
            </div>
            <Button type="submit" className="w-full" loading={loading} size="lg">
              Sign In <ArrowRight size={16}/>
            </Button>
            <div className="flex items-center justify-between">
              <button type="button" onClick={()=>{setStep('phone');setOtp('');setDevOtp('');setPassword('');setError('');}}
                className="text-[13px] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors flex items-center gap-1.5">
                <RefreshCw size={13}/> Change number
              </button>
              <button type="button" onClick={startForgotPassword}
                className="text-[13px] font-medium text-[var(--accent)] hover:underline">
                Forgot password?
              </button>
            </div>
          </form>
        )}

        {/* Step — New password (forgot-password flow) */}
        {step === 'new-password' && (
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-[12px] bg-[var(--accent)]/10 flex items-center justify-center">
                <KeyRound size={20} className="text-[var(--accent)]"/>
              </div>
              <div>
                <h2 className="text-[18px] font-bold text-[var(--text-primary)]">Set a new password</h2>
                <p className="text-[12px] text-[var(--text-secondary)]">+91 {phone} · OTP verified</p>
              </div>
            </div>
            <div className="space-y-1.5">
              <label className="text-[13px] font-semibold text-[var(--text-primary)]">New Password</label>
              <input type="password" value={newPassword} onChange={e=>setNewPassword(e.target.value)} placeholder="At least 6 characters" autoFocus
                className="w-full h-10 px-3 bg-[var(--bg-card)] border border-[var(--border)] rounded-[10px] text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] transition-all"/>
            </div>
            <div className="space-y-1.5">
              <label className="text-[13px] font-semibold text-[var(--text-primary)]">Confirm Password</label>
              <input type="password" value={confirmNewPassword} onChange={e=>setConfirmNewPassword(e.target.value)} placeholder="Re-enter password"
                className="w-full h-10 px-3 bg-[var(--bg-card)] border border-[var(--border)] rounded-[10px] text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] transition-all"/>
            </div>
            {error && <p className="text-[12px] text-[var(--danger)]">{error}</p>}
            <Button type="submit" className="w-full" loading={loading} size="lg">
              Update Password <ArrowRight size={16}/>
            </Button>
          </form>
        )}

        {/* Step — New company registration */}
        {step === 'register' && (
          <form onSubmit={handleRegister} className="space-y-4">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-10 h-10 rounded-[12px] bg-[var(--accent)]/10 flex items-center justify-center">
                <Building2 size={20} className="text-[var(--accent)]"/>
              </div>
              <div>
                <h2 className="text-[18px] font-bold text-[var(--text-primary)]">Set up your company</h2>
                <p className="text-[12px] text-[var(--text-secondary)]">+91 {phone} — let's get started</p>
              </div>
            </div>

            <div className="space-y-1.5">
              <label className="text-[13px] font-semibold text-[var(--text-primary)]">Your Full Name</label>
              <input type="text" value={ownerName} onChange={e=>setOwnerName(e.target.value)} placeholder="e.g. Tushar Sharma" autoFocus
                className="w-full h-10 px-3 bg-[var(--bg-card)] border border-[var(--border)] rounded-[10px] text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] transition-all"/>
            </div>

            <div className="space-y-1.5">
              <label className="text-[13px] font-semibold text-[var(--text-primary)]">Company Name</label>
              <input type="text" value={companyName} onChange={e=>setCompanyName(e.target.value)} placeholder="e.g. Curtina Technologies Pvt. Ltd."
                className="w-full h-10 px-3 bg-[var(--bg-card)] border border-[var(--border)] rounded-[10px] text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] transition-all"/>
            </div>

            <div className="space-y-1.5">
              <label className="text-[13px] font-semibold text-[var(--text-primary)]">State</label>
              <select value={state} onChange={e=>setState(e.target.value)}
                className="w-full h-10 px-3 bg-[var(--bg-card)] border border-[var(--border)] rounded-[10px] text-[14px] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] transition-all">
                {INDIAN_STATES.map(([code,name])=>(
                  <option key={code} value={code}>{name}</option>
                ))}
              </select>
            </div>

            <div className="space-y-1.5">
              <label className="text-[13px] font-semibold text-[var(--text-primary)]">Create Password</label>
              <input type="password" value={registerPassword} onChange={e=>setRegisterPassword(e.target.value)} placeholder="At least 6 characters"
                className="w-full h-10 px-3 bg-[var(--bg-card)] border border-[var(--border)] rounded-[10px] text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] transition-all"/>
            </div>

            <div className="space-y-1.5">
              <label className="text-[13px] font-semibold text-[var(--text-primary)]">Confirm Password</label>
              <input type="password" value={confirmRegisterPassword} onChange={e=>setConfirmRegisterPassword(e.target.value)} placeholder="Re-enter password"
                className="w-full h-10 px-3 bg-[var(--bg-card)] border border-[var(--border)] rounded-[10px] text-[14px] text-[var(--text-primary)] placeholder:text-[var(--text-tertiary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)] transition-all"/>
            </div>

            {error && <p className="text-[12px] text-[var(--danger)]">{error}</p>}
            <Button type="submit" className="w-full" loading={loading} size="lg">
              Create Company & Choose Plan <ArrowRight size={16}/>
            </Button>
          </form>
        )}
      </div>

      <p className="text-center text-[11px] text-[var(--text-tertiary)] mt-5">
        {step === 'register' ? `Registering +91 ${phone}` : 'Phase 1 · Local Dev'}
      </p>
    </div>
  );
}
